'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAppUserByEmail, updateLastLogin } from '@/lib/services/userService';

export type UserRole = 'admin' | 'field';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  siteId?: string | null;
  assignedSites?: string[];
  permissions?: {
    scanAttendance: boolean;
    viewWorkers: boolean;
    viewPayments: boolean;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string, role: UserRole) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'decorholdings_auth_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string, role: UserRole): Promise<{ success: boolean; error?: string }> => {
    try {
      const dbUser = await getAppUserByEmail(email);

      if (!dbUser) {
        return { success: false, error: 'Invalid credentials. Please check your email and password.' };
      }

      if (dbUser.passwordHash !== password) {
        return { success: false, error: 'Invalid credentials. Please check your email and password.' };
      }

      if (dbUser.role !== role) {
        return { success: false, error: `This account is not registered as a ${role === 'admin' ? 'Administrator' : 'Field Agent'}. Please select the correct role.` };
      }

      if (dbUser.status !== 'active') {
        return { success: false, error: 'Your account has been suspended or deactivated. Please contact an administrator.' };
      }

      const authUser: AuthUser = {
        id: dbUser.id,
        name: `${dbUser.firstName} ${dbUser.lastName}`,
        email: dbUser.email,
        role: dbUser.role,
        siteId: dbUser.assignedSites?.[0] ?? null,
        assignedSites: dbUser.assignedSites,
        permissions: dbUser.permissions,
      };

      setUser(authUser);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(authUser)); } catch { /* ignore */ }

      // Update last login in background
      updateLastLogin(dbUser.id).catch(() => {});

      return { success: true };
    } catch {
      return { success: false, error: 'Unable to connect. Please try again.' };
    }
  };

  const logout = () => {
    setUser(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
